import { useContext, useRef, useState } from 'react'
import { useFormikContext, Field } from 'formik'
import { motion } from "framer-motion"
import classnames from 'classnames/bind'
import * as Yup from 'yup'
import { StoreContext } from '~/store'
import { FormContent, FormHeader, PackageBookingSummary, ButtonDrawer, RadioField, FieldDropDown, InputField, BookingSummaryField, Modal, EditField } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { wrapWithValidation } from '~/utils'
import { usePrevious } from '~/utils/hooks'
import * as MotionElement from '~/components/ui/MotionElement'
import * as common from '~/config/common.module.css'
import * as panelStyles from '~/components/ui/OptionsPanel/index.module.css'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'

const cx = classnames.bind(common)
const cxPanel = classnames.bind(panelStyles)

const getPaymentText = (value, accounts) => {
  if (value !== 'PayInCar') {
    return accounts.find(a => a.accountNumber === parseInt(value))?.accountName
  }
  return i18next.t('title-pay-in-car-sender')
}

const SelectSender = ({fullscreen, currentView}) => {
  const { t } = useTranslation()
  const { prev, next } = useContext(StepperContext)
  const { user, paymentAccounts, setPreferredPaymentAccount } = useContext(StoreContext)
  const { values, setFieldValue } = useFormikContext()

  const paymentButtonRef = useRef()
  const payInCarRef = useRef()

  const wrappedPrev = () => {
    prev('what')
  }

  const prevAccounts = usePrevious(user?.accounts)
  const prevPaymentAccounts = usePrevious(paymentAccounts)

  const disableCreditCards = !values.priceType?.paymentMethods?.includes('CreditCard')
  const disableTSABAccounts = !values.priceType?.paymentMethods?.includes('TSABAccount')
  const disableTCAAccounts = !values.priceType?.paymentMethods?.includes('TravelClearingAccount')

  const showReference = values?.company !== false || values?.account?.type === 'TSAB' ||  values?.account?.type === 'TCA'
  const needReference = values?.company?.requireReference === true || 
        (values?.account !== false && (values?.account?.type ==='TSAB' || values?.account?.type ==='TCA') && values?.account?.flags?.includes('reference'))

  const referenceFieldRef = useRef()
  const [referenceIsOpen, setReferenceIsOpen] = useState(false);
  const toggleReference = () => {
    setReferenceIsOpen(!referenceIsOpen)
  }

  const wrappedNext = wrapWithValidation(next, useFormikContext())

  const setAccount = account => {
    if (!account) {
      setFieldValue('accountReference', '', false)
    }
    setFieldValue('account', account, false)
  }
  const buttons = [
    { func: wrappedPrev, isBack: true },
    { func: wrappedNext, title: t('button-continue') },
  ]

  return (
    <div layout className={cx(common.widgetWrapper, { fullscreen:fullscreen })}>
      <FormContent>
        <FormHeader onlyFullscreen>{t('your-trip')}</FormHeader> 
        <FormHeader onlyMobile>{t('header-whos-the-sender')}</FormHeader>
        <PackageBookingSummary values={values} currentView={currentView} showPayment/>
      </FormContent>
      <MotionElement.div className={common.contentBottom}>
        <FormHeader onlyFullscreen>{t('header-whos-the-sender')}</FormHeader>
        <Field name="name" placeholder={t('placeholder-name')} component={InputField} border />
        <Field name="phone" placeholder={t('placeholder-phone')} component={InputField} border />
        <Field name="packageDeliveryInfo.packageSender.nameOnDoor" placeholder={t('placeholder-name-on-door')} component={InputField} border />
        <Field name="packageDeliveryInfo.packageSender.numberOfStairs" placeholder={t('placeholder-number-of-stairs')} component={InputField} border />
        <Field name="packageDeliveryInfo.packageSender.doorCode" placeholder={t('placeholder-door-code')} component={InputField} border />


        <div className={cxPanel(panelStyles.title)}>{t('title-payment')}</div>
        <Field type="radio" icon="PayInCar" name="payment" title={t('title-pay-in-car')} value="PayInCar" onClick={()=>setAccount(false)} innerRef={payInCarRef} component={RadioField} />
        {/* paymentAccounts?.map((account, index) => (
          <Field 
            key={index} 
            type="radio" 
            name="payment" 
            icon={`Card${account.cardType}`} 
            title={account.maskedAccount} 
            info={account.cardType} 
            value={`CreditCard-${account.accountId}`} 
            onClick={()=>setAccount(account)} 
            tabIndex="-1" 
            disabled={disableCreditCards } 
            component={RadioField} 
          />
        )) */}
        {user?.accounts?.map((account, index) => (
          <Field 
            key={index} 
            type="radio" 
            name="payment" 
            icon="TSAB"
            title={account.accountName} 
            info={account.accountNumber} 
            value={`Account-${account.accountNumber}`} 
            onClick={()=>setAccount(account)} 
            tabIndex="0"
            disabled={
              (account.type === 'TSAB' && disableTSABAccounts ) 
              || (account.type === 'TCA' && disableTCAAccounts)
            }
            component={RadioField} 
          />
        ))}
        {showReference && <>
            <BookingSummaryField 
              title={t('placeholder-reference')} 
              value={(!values.accountReference || values.accountReference.trim()==='') ? (needReference ? t('validation-need-reference') : '') : values.accountReference}
              icon="Reference" changeToggle={toggleReference} 
              required
              empty={(!values.accountReference || values.accountReference.trim()==='') && needReference ? true : false}>
            </BookingSummaryField>
            <Modal isOpen={referenceIsOpen} onClose={toggleReference}>
              <EditField fields={[{id:"accountReference", title:t('placeholder-reference')}]} title={t('placeholder-reference')} innerRef={referenceFieldRef} onClose={toggleReference} />
            </Modal>
            <div className={common.hidden}>
              <Field name="accountReference" placeholder={t('placeholder-reference')} maxLength="40" component={InputField} />
            </div>
          </>}
      </MotionElement.div>
      <ButtonDrawer buttons={buttons} fullscreen={fullscreen}/>
    </div>
  )
}

SelectSender.validationSchema = Yup.object({
  name: Yup.string().required(i18next.t('validation-need-name')),
  phone: Yup.string()
    .matches(/^\+?\d+$/, i18next.t('validation-only-numbers'))
    .required(i18next.t('validation-need-phone')),
  packageDeliveryInfo: Yup.object({
    packageSender: Yup.object({
      numberOfStairs: Yup.string()
        .matches(/^\+?\d+$/, i18next.t('validation-only-numbers')),
      doorCode: Yup.string(),
    })
  }),
  accountReference: Yup.string()
    .when('account', (account) => {
      return account && account?.flags && account?.flags?.includes('reference') ? Yup.string().required(i18next.t('validation-need-reference')) : Yup.string()
    }),
})
 
export default SelectSender
